import { auth } from '../firebaseConfig';

// Base URL for the API
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.fusshn.com'; // Fallback for local dev
// const API_BASE_URL = 'http://0.0.0.0:8000'

/**
 * Build an Authorization header from the current Firebase user's ID token.
 * Returns an empty object when no user is signed in, so public endpoints
 * (events, slots, tickets, etc.) are unaffected.
 * @returns {Promise<{Authorization?: string}>}
 */
async function getAuthHeader() {
    const currentUser = auth.currentUser;
    if (!currentUser) return {};
    try {
        const token = await currentUser.getIdToken();
        return { Authorization: `Bearer ${token}` };
    } catch (e) {
        console.warn('api_service: could not get Firebase token', e);
        return {};
    }
}

/**
 * Generic helper for making API requests.
 * @param {string} endpoint - The API endpoint (relative to base URL).
 * @param {object} options - Fetch options (method, headers, body, etc.).
 * @returns {Promise<any>} - The JSON response data.
 */
async function request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const defaultHeaders = {
        'Content-Type': 'application/json',
    };

    // Attach a Firebase ID token when a user is signed in. Public endpoints
    // ignore it; logged-out users send no Authorization header.
    const authHeader = await getAuthHeader();

    const config = {
        ...options,
        headers: {
            ...defaultHeaders,
            ...authHeader,
            ...options.headers,
        },
    };

    if (config.body && typeof config.body === 'object') {
        config.body = JSON.stringify(config.body);
    }

    try {
        let response = await fetch(url, config);

        // A 401 mid-session usually means the cached token expired. Force-refresh
        // the Firebase token once and retry, then fall through to normal handling.
        if (response.status === 401 && auth.currentUser) {
            try {
                const freshToken = await auth.currentUser.getIdToken(true);
                config.headers.Authorization = `Bearer ${freshToken}`;
                response = await fetch(url, config);
            } catch (e) {
                console.warn('api_service: token refresh retry failed', e);
            }
        }

        // Handle non-200 responses
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            // Coupon (and other FastAPI) endpoints return their user-facing message
            // under `detail` — either a plain string or { code, message }. Fall back
            // to it so friendly errors like "This coupon has expired" reach the user.
            const detailMsg = typeof errorData.detail === 'object'
                ? errorData.detail?.message
                : errorData.detail;
            const msg = (typeof errorData.message === 'object'
                ? errorData.message?.message
                : errorData.message) || detailMsg;
            throw new Error(msg || `API Error: ${response.status} ${response.statusText}`);
        }

        // Return JSON data
        // Some endpoints might return 204 No Content
        if (response.status === 204) {
            return null;
        }

        const data = await response.json();
        return data;

    } catch (error) {
        console.error(`API Request failed for ${endpoint}:`, error);
        throw error;
    }
}

/**
 * Service class for handling all backend API interactions.
 */
export const ApiService = {
    // --- User Management ---

    /**
     * Create a new user.
     * @param {object} userData - User data.
     */
    createUser: async (userData) => {
        return request('/user', {
            method: 'POST',
            body: userData,
        });
    },

    /**
     * Get all users.
     */
    getAllUsers: async () => {
        return request('/user', {
            method: 'GET',
        });
    },

    /**
     * Get a single user by UID.
     * @param {string} uid - User ID.
     */
    getSingleUser: async (uid) => {
        return request(`/user/${uid}`, {
            method: 'GET',
        });
    },

    /**
     * Update a user.
     * @param {string} uid - User ID.
     * @param {object} data - Fields to update.
     */
    updateUser: async (uid, data) => {
        return request(`/user/${uid}`, {
            method: 'PUT',
            body: data,
        });
    },

    /**
     * Delete a user.
     * @param {string} uid - User ID.
     */
    deleteUser: async (uid) => {
        return request(`/user/${uid}`, {
            method: 'DELETE',
        });
    },

    // --- Event Management ---

    /**
     * Get all events.
     */
    getAllEvents: async () => {
        return request('/events', {
            method: 'GET',
        });
    },

    /**
     * Get a single event.
     * @param {string} eventId 
     */
    getSingleEvent: async (eventId) => {
        return request(`/event/${eventId}`, {
            method: 'GET',
        });
    },

    /**
     * Get all dates for an event.
     * @param {string} eventId 
     */
    getAllEventDates: async (eventId) => {
        return request(`/event/${eventId}/dates`, {
            method: 'GET',
        });
    },

    /**
     * Get a single event date.
     * @param {string} eventId 
     * @param {string} dateId 
     */
    getSingleEventDate: async (eventId, dateId) => {
        return request(`/event/${eventId}/date/${dateId}`, {
            method: 'GET',
        });
    },

    /**
     * Get all slots for an event date.
     * @param {string} eventId 
     * @param {string} dateId 
     */
    getAllSlots: async (eventId, dateId) => {
        return request(`/event/${eventId}/date/${dateId}/slots`, {
            method: 'GET',
        });
    },

    /**
     * Get a single slot.
     * @param {string} eventId 
     * @param {string} dateId 
     * @param {string} slotId 
     */
    getSingleSlot: async (eventId, dateId, slotId) => {
        return request(`/event/${eventId}/date/${dateId}/slot/${slotId}`, {
            method: 'GET',
        });
    },

    /**
     * Get all slots for an event (using new flat API).
     * @param {string} eventId 
     */
    getSlots: async (eventId) => {
        return request(`/slots?eventId=${eventId}`, {
            method: 'GET',
        });
    },

    /**
     * Get all tickets for a slot.
     * @param {string} eventId 
     * @param {string} dateId 
     * @param {string} slotId 
     */
    getAllTickets: async (eventId, dateId, slotId) => {
        return request(`/event/${eventId}/date/${dateId}/slot/${slotId}/tickets`, {
            method: 'GET',
        });
    },

    /**
     * Get a single ticket.
     * @param {string} eventId 
     * @param {string} dateId 
     * @param {string} slotId 
     * @param {string} ticketId 
     */
    getSingleTicket: async (eventId, dateId, slotId, ticketId) => {
        return request(`/event/${eventId}/date/${dateId}/slot/${slotId}/ticket/${ticketId}`, {
            method: 'GET',
        });
    },

    /**
     * Get all event dates for an event (new API).
     * @param {string} eventId 
     */
    getEventDates: async (eventId) => {
        return request(`/eventDates?event_id=${eventId}`, {
            method: 'GET',
        });
    },

    /**
     * Get all slots for an event date (new API).
     * @param {string} eventId 
     * @param {string} dateId 
     */
    getEventSlots: async (eventId, dateId) => {
        return request(`/slots?event_id=${eventId}&date_id=${dateId}`, {
            method: 'GET',
        });
    },

    /**
     * Get all tickets for a slot (new API).
     * @param {string} eventId 
     * @param {string} dateId 
     * @param {string} slotId 
     */
    getEventTickets: async (eventId, dateId, slotId) => {
        return request(`/tickets?event_id=${eventId}&date_id=${dateId}&slot_id=${slotId}`, {
            method: 'GET',
        });
    },

    /**
     * Update a ticket.
     * @param {string} ticketId 
     * @param {object} data 
     * @param {string} eventId 
     * @param {string} dateId 
     * @param {string} slotId 
     */
    updateTicket: async (ticketId, data, eventId, dateId, slotId) => {
        // Construct query params if needed or just use the ID
        // Based on apis_ref.md: 
        // Endpoint: PUT /ticket/{ticket_id}
        // Query Parameters: event_id, date_id, slot_id
        const queryParams = new URLSearchParams({
            event_id: eventId,
            date_id: dateId,
            slot_id: slotId
        }).toString();

        return request(`/ticket/${ticketId}?${queryParams}`, {
            method: 'PUT',
            body: data
        });
    },


    // --- Booking Management ---

    /**
     * Get all bookings.
     * @param {string[]} ids - Optional list of booking IDs to filter.
     */
    getAllBookings: async (ids = []) => {
        let endpoint = '/bookings';
        if (ids && ids.length > 0) {
            const queryString = ids.map(id => `ids=${id}`).join('&');
            endpoint += `?${queryString}`; // Check if backend supports this array format or comma separated
        }
        return request(endpoint, {
            method: 'GET',
        });
    },

    /**
     * Create a new booking.
     * @param {object} bookingData 
     */
    createBooking: async (bookingData) => {
        return request('/booking', {
            method: 'POST',
            body: bookingData,
        });
    },

    /**
     * Confirm a booking after successful payment.
     * Handles ticket availability, booking creation, user profile update, and confirmation email.
     * @param {object} confirmData - Booking confirmation payload.
     */
    confirmBooking: async (confirmData) => {
        return request('/booking/confirm', {
            method: 'POST',
            body: confirmData,
        });
    },

    /**
     * Update a booking.
     * @param {string} bookingId 
     * @param {object} bookingData 
     */
    updateBooking: async (bookingId, bookingData) => {
        return request(`/booking/${bookingId}`, {
            method: 'PUT',
            body: bookingData,
        });
    },

    // --- Miscellaneous ---

    /**
     * Get all coupons.
     */
    getCoupons: async () => {
        return request('/coupons', {
            method: 'GET',
        });
    },

    /**
     * Get coupons for a specific event.
     * @param {string} eventId
     * @param {string} [userId] - Optional. Server filters user-restricted coupons when provided.
     */
    getEventCoupons: async (eventId, userId) => {
        const query = userId ? `?user_id=${userId}` : '';
        return request(`/events/${eventId}/coupons${query}`, {
            method: 'GET',
        });
    },

    /**
     * Validate a coupon against the current cart. Read-only — does not mark coupon as used.
     * @param {{ coupon_code, user_id, event_id, tickets: Array<{ticket_type_id, quantity, price_per_ticket}> }} payload
     */
    validateCoupon: async (payload) => {
        return request('/coupons/validate', {
            method: 'POST',
            body: payload,
        });
    },

    /**
     * Redeem a coupon after payment is confirmed. Increments usage counters.
     * @param {{ coupon_code, user_id, event_id, order_id, tickets: Array<{ticket_type_id, quantity, price_per_ticket}> }} payload
     */
    redeemCoupon: async (payload) => {
        return request('/coupons/redeem', {
            method: 'POST',
            body: payload,
        });
    },

    /**
     * Get app versions.
     */
    getAppVersions: async () => {
        return request('/app-versions', {
            method: 'GET',
        });
    },

    /**
     * Get the platform-wide terms and conditions. Public endpoint — the auth
     * header is attached when signed in but ignored by the server.
     * @param {AbortSignal} [signal] - Optional. Aborts the request on unmount.
     */
    getTermsAndConditions: async (signal) => {
        return request('/tnc', {
            method: 'GET',
            signal,
        });
    },

    /**
     * Get the privacy policy. Public endpoint — the auth header is attached
     * when signed in but ignored by the server.
     * @param {AbortSignal} [signal] - Optional. Aborts the request on unmount.
     */
    getPrivacyPolicy: async (signal) => {
        return request('/privacy-policy', {
            method: 'GET',
            signal,
        });
    }
};